<template>
    <div>
        <div class="bg-body-light">
            <div class="content content-full">
                <div class="d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-center py-2">
                    <div class="flex-grow-1">
                        <h1 class="h3 fw-bold mb-1">
                            <span v-if="!editingTitle" @click="startEditingTitle" style="cursor: pointer;">
                                {{ account.title }}
                                <i class="fa fa-pen ms-2 text-muted" style="font-size: 0.8em;"></i>
                            </span>
                            <input v-else v-model="editedTitle" @blur="saveTitle" @keyup.enter="saveTitle" type="text"
                                class="form-control form-control-sm" style="max-width: 300px; display: inline-block;"
                                autofocus />
                        </h1>
                        <h2 class="h6 fw-medium text-muted mb-0">
                            <span class="badge p-2 text-uppercase" :class="`bg-${account.type.color}`">
                                {{ account.type.title_ar }}
                            </span>
                        </h2>
                    </div>
                    <a v-if="account.user" class="block block-rounded block-link-pop me-4" :href="`/profile/${account.user._id}`">
                        <div class="block-content block-content-full d-flex align-items-center justify-content-between">
                            <img class="img-avatar img-avatar48" :src="account.user.avatar" alt="">
                            <div class="ms-3 text-end">
                                <p class="fw-semibold mb-0">{{ account.user.first_name }} {{ account.user.last_name }}</p>
                                <p class="fs-sm fw-medium text-muted mb-0">
                                    <span class="badge bg-primary">{{ account.user.role.role_title }}</span>
                                </p>
                            </div>
                        </div>
                    </a>
                    <a class="block block-rounded px-2 block-link-shadow" :class="`bg-${account.type.color}`"
                        href="javascript:void(0)">
                        <div class="block-content block-content-full d-flex align-items-center justify-content-between">
                            <div>
                                <i :class="'fa-2x ' + getIconForType(account.type_id) + ' text-white-50'"></i>
                            </div>
                            <dl class="ms-3 text-end mb-0">
                                <dt class="text-white h3 fw-extrabold mb-0">
                                    <span v-html="formatWithSmallDecimals(account.balance)"></span>
                                </dt>
                                <dd class="text-white fs-sm fw-medium text-muted mb-0">
                                    {{ $t('table_balance') }}
                                </dd>
                            </dl>
                        </div>
                    </a>
                </div>
            </div>
        </div>
        <meta name="csrf-token" :content="csrfToken" />
        <!-- Page Content -->
        <div class="content">
            <transaction-table :transactions="localTransactions" @filters-updated="fetchTransactions"
                @transaction-reverted="fetchTransactions" />
        </div>
    </div>
</template>

<script>
import {
    formatNumberShort,
    formatNumberWithCommas,
    formatDate
} from '@/utils'
import swal from '../../../utils/swal';
export default {
    props: {
        initialAccount: Object,
        accountStatus: Array,
        grouped_users: Array,
        initialErrors: Array,
        permissions: Object,
        user: Object,
    },

    data() {
        return {
            account: { ...this.initialAccount }, // Create a copy of initialAccount
            localTransactions: [], // Initialize localTransactions
            errorMessages: this.initialErrors || [],
            csrfToken: document.querySelector('meta[name="csrf-token"]').content,
            isModalVisible: false,
            isLoading: false,
            feedbackTapLoading: false,
            logTapLoading: false,
            historyCount: 0,
            timelineEvents: [],
            feedbacks: [],
            iconMapping: {
                '1': 'fa-solid fa-coins',              // For type_id = '1'
                '2': 'fa-solid fa-arrows-down-to-people',      // For type_id = '2'
                '3': 'fa-solid fa-chart-column',      // For type_id = '3'
                '4': 'fa-solid fa-money-bill-trend-up',     // For type_id = '4'
                '5': 'fa-solid fa-cart-shopping',                   // For type_id = '5'
            },
            editingTitle: false,
            editedTitle: '',
        }
    },

    mounted() {
        this.fetchTransactions();
        console.log("Account Data:", this.account);
        $('.selectpicker').selectpicker();
    },

    methods: {
        getIconForType(typeId) {
            return this.iconMapping[typeId] || 'fa-question-circle'; // Default icon
        },
        async hasPermission(permission) {
            try {
                const response = await axios.get(`/partner-users/check-permission/${permission}`);
                return response.data.hasPermission;
            } catch (error) {
                console.error('Error checking permission:', error);
                return false;
            }
        },

        formatWithSmallDecimals(value) {
            const formatted = formatNumberWithCommas(value)
            const [intPart, decimalPart] = formatted.split('.')

            if (decimalPart) {
                return `${intPart}.<small style="font-size: 0.7em; font-weight: normal;">${decimalPart}</small>`
            } else {
                return intPart
            }
        },

        async fetchTransactions(filters = {}, page = 1, perPage = 50) {
            if (window.One) window.One.layout('header_loader_on');
            this.fetchingTransactions = true;

            const cleanedFilters = this.cleanFilters(filters);

            try {
                const response = await axios.post(`/transactions/account/${this.account._id}`, {
                    ...cleanedFilters,
                    page,
                    perPage
                }, {
                    headers: {
                        'X-CSRF-TOKEN': this.csrfToken,
                        'Content-Type': 'application/json'
                    }
                });

                this.localTransactions = response.data.transactions;
                this.total = response.data.total;
            } catch (error) {
                console.error('Error fetching transactions:', error);
                this.showErrorNotification('Failed to fetch transactions.');
            } finally {
                this.fetchingTransactions = false;
                if (window.One) window.One.layout('header_loader_off');
            }
        },
        cleanFilters(filters) {
            const cleaned = {}
            for (const key in filters) {
                const value = filters[key]
                if (
                    value === null ||
                    value === '' ||
                    (Array.isArray(value) && value.length === 0)
                ) continue
                cleaned[key] = value
            }
            return cleaned
        },
        startEditingTitle() {
            this.editedTitle = this.account.title;
            this.editingTitle = true;
        },

        async saveTitle() {
            if (this.editedTitle !== this.account.title) {
                try {
                    this.isLoading = true; // Optional loading
                    const response = await axios.post(`/accounts/update-title/${this.account._id}`, {
                        title: this.editedTitle,
                    }, {
                        headers: {
                            'X-CSRF-TOKEN': this.csrfToken,
                        },
                    });

                    if (response.data.status === 'success') {
                        this.account.title = this.editedTitle;
                        swal.success(this.$t('message_account_title_updated'));
                    } else {
                        swal.error(this.$t('message_general_error'));
                    }
                } catch (error) {
                    console.error('Failed to update title:', error);
                    swal.error(this.$t('message_general_error'));
                } finally {
                    this.isLoading = false;
                }
            }
            this.editingTitle = false;
        },
        showErrorNotification(message) {
            swal.error(this.$t('message_general_error'));
        }
    }
}
</script>

<style scoped>
/* Add your component-specific styles here */
</style>
